import React, { useState, useEffect, createContext, useCallback } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Paper,
  TablePagination,
  TableFooter,
  IconButton,
  Skeleton,
} from '@mui/material'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import { useGetUsers } from '../../api/user'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { UserRow } from './UserRow'
import { SearchUser } from './SearchUser'
import { debounce } from '@mui/material'
import ErrorMessage from '../shared/component/ErrorMessage'

const initState = {
  refetch: undefined,
}
export const UserContext = createContext(initState)

export const UserTable = () => {
  const [key, setKey] = useState('')
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const pageParam = Number(query.get('page'))
  const limitParam = Number(query.get('limit'))
  useEffect(() => {
    if (Number.isInteger(pageParam)) {
      setPage(pageParam)
    }
    if (limitParam) {
      setLimit(limitParam)
    }
  }, [limitParam, pageParam])

  const [page, setPage] = useState(Number.isInteger(pageParam) ? pageParam : 0)
  const [limit, setLimit] = useState(limitParam ? limitParam : 10)

  const { data, refetch, isLoading } = useGetUsers({ page: page + 1, limit: limit, key: key })

  const userData = data?.docs ?? []

  useEffect(() => {
    refetch()
    window.scrollTo(0, 0)
  }, [limit, page, key])

  const debounceRefetch = useCallback(
    debounce(() => {
      refetch()
    }, 500),
    [],
  )

  useEffect(() => {
    debounceRefetch()
  }, [limit, page, key])

  const navigate = useNavigate()

  const handleChangePage = (event, newPage) => {
    navigate(`/admin?page=${newPage}&limit=${limit}`)
  }
  const handleChangeRowsPerPage = (event) => {
    if (limit) {
      navigate(`/admin?page=1&limit=${event.target.value}`)
    }
  }

  return (
    <UserContext.Provider value={{ refetch }}>
      <Box mb={2}>
        <SearchUser setKey={setKey} limit={limit} />
      </Box>
      {isLoading || userData?.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label='simple table'>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell align='right'>First Name</TableCell>
                  <TableCell align='right'>Last Name</TableCell>
                  <TableCell align='right'>Official Name</TableCell>
                  <TableCell align='right'>Employee ID</TableCell>
                  <TableCell align='right'>Position</TableCell>
                  <TableCell align='right'></TableCell>
                  <TableCell align='right'></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading
                  ? Array(limit)
                      .fill()
                      .map((_, idx) => <SkeletonRow key={idx} height={30} />)
                  : userData.map((row, idx) => <UserRow key={idx} row={row} />)}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    colSpan={6}
                    count={data?.totalDocs}
                    rowsPerPage={limit}
                    page={page}
                    SelectProps={{
                      inputProps: {
                        'aria-label': 'rows per page',
                      },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </>
      ) : (
        <ErrorMessage empty />
      )}
    </UserContext.Provider>
  )
}

const SkeletonRow = React.memo(({ height }) => (
  <TableRow>
    <TableCell colSpan='100%' height={height}>
      <Skeleton width='100%' height='100%' />
    </TableCell>
  </TableRow>
))

function TablePaginationActions(props) {
  const theme = useTheme()
  const { count, page, rowsPerPage, onPageChange } = props

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1)
  }

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label='first page'
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label='previous page'>
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='next page'
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='last page'
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  )
}
